import React, { useCallback, useMemo } from "react"
import { Routes, Route } from "react-router-dom"
import _ from "lodash"

import { useVenue } from "providers/venue"

// Theme
import { generateTheme } from "theme/generateTheme"
import { appThemeOptionFactory as directoryThemeOptionFactory } from "./theme/app/directory"

import { ThemeProvider } from "providers/mui-extended/ThemeProvider"

import { HomePage } from "pages/home"
import { MapOrdinal } from "pages/maps/[ordinal]"
import { Detail as PlaceDetailPage } from "pages/maps/place/[placeId]"
import { Layout as MapLayout } from "pages/maps/_layout"
import { DirectionLayout } from "pages/maps/dir/[origin]/[destination]/_layout"

import { DirectionOverviewPage } from "pages/maps/dir/[origin]/[destination]"
import { StepByStep } from "pages/maps/dir/[origin]/[destination]/navigate/steps/[step]"

import { AppLayout } from "layouts/AppLayout"

import { ScrollToTop } from "components/ScrollToTop"

import { homeNavigations, standardRoutes } from "standard"

import { PageLayout } from "layouts/PageLayout"

import { useIdleTimer } from "react-idle-timer"
import { useNavigate } from "react-router-dom"
import { AmenityByCategory } from "./pages/maps/amenities/[category]"

import {
  getAllExtensionRoutes,
  getExtensionThemeFactories,
  mergeRoutes,
} from "./utils/extension"
import { extensionConfigs } from "./extensions"

import config from "./config"
import { LoadingScreen } from "./components/LoadingScreen"
import { AnimatePresence } from "framer-motion"
import { useAppUI } from "./providers/ui"
import { MotionDiv, showContent } from "./components/motions"

const resetEvent = new Event("appreset")

// Extension routes
const extensionRoutes = getAllExtensionRoutes(
  config("venueExtension"),
  extensionConfigs
)

// Merge Standard + Extension routes
const mergedRoutes = mergeRoutes(
  _.get(standardRoutes, config("device", "mobile")),
  extensionRoutes
)

// Extension Themes
const extensionThemeConfig = getExtensionThemeFactories(
  config("venueExtension"),
  extensionConfigs
)

// App Theme Factory
const appThemeOptionFactory = _.get(
  directoryThemeOptionFactory,
  config("device", "mobile")
)

export const StandardApp = () => {
  // Generate a venue theme
  const { themeOptions: venueThemeOptions, dataLoaded } = useVenue()
  const { mapReady } = useAppUI()
  const theme = generateTheme(venueThemeOptions, appThemeOptionFactory, {
    extendThemeFactories: extensionThemeConfig.themeFactories,
    ignoreAppThemeFactory: extensionThemeConfig.ignoreAppThemeFactory,
  })
  const navigate = useNavigate()
  const isAppReady = useMemo(
    () => mapReady && dataLoaded,
    [mapReady, dataLoaded]
  )
  const onIdle = useCallback(() => {
    navigate("/")
    window.dispatchEvent(resetEvent)
  }, [navigate])

  useIdleTimer({
    onIdle,
    timeout: 1000 * 60 * 5,
    throttle: 500,
    disabled: config("device", "mobile") === "mobile",
  })

  return (
    <ThemeProvider theme={theme}>
      <ScrollToTop />
      <AnimatePresence mode="sync">
        {!isAppReady && <LoadingScreen />}
        <MotionDiv
          key="content"
          variants={showContent}
          initial="initial"
          animate="animate"
        >
          <Routes>
            <Route path="/" element={<AppLayout />}>
              {/*//TODO:: custom home page for kiosk later */}
              <Route
                index
                element={<HomePage navigations={homeNavigations} />}
              />
              {/*//TODO:: custom map layout for kiosk later */}
              <Route path="/maps/*" element={<MapLayout />}>
                {/* The indoorPage parent router treats the route child parameter as the same parameter as its own. */}
                <Route path="ordinal/:ordinal" element={<MapOrdinal />} />
                <Route path="place/:placeId" element={<PlaceDetailPage />} />
                <Route
                  path="amenities/:category"
                  element={<AmenityByCategory />}
                />
                <Route
                  path="dir/:origin/:destination"
                  element={<DirectionLayout />}
                >
                  <Route index element={<DirectionOverviewPage />} />
                  <Route path="navigate/steps/:step" element={<StepByStep />} />
                </Route>
              </Route>
              <Route path="/*" element={<PageLayout />}>
                {/* Add or override specific route extension pages here. */}
                {mergedRoutes.map(({ path, ...routeProps }) => (
                  <Route key={path} path={path} {...routeProps} />
                ))}
              </Route>
            </Route>
          </Routes>
        </MotionDiv>
      </AnimatePresence>
    </ThemeProvider>
  )
}
